/**
 * frontend/src/pages/valuation/_ProduceDocumentCard.tsx
 *
 * VALUATION-B3 sign-off — the Produce document card.
 *
 * Split out of _SignOffGates.tsx under docs/FILE_SIZE_POLICY.md.
 */
import { EmailDocumentButton } from './_EmailDocument'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { ValuationDocumentRow } from '@/api/valuation'
import { IssuedDocumentList } from './_OverviewDocuments'
import { Gate } from './_SignOffGates'
import { producedReasonText } from './_FinalizeCard'

export function ProduceDocumentCard({
  engagementId,
  canProduce,
  note,
  onProduce,
  producing,
  documents,
  pdfStatus,
  concluded,
  liveVersion,
  documentsOnFile,
  owner = false,
}: {
  engagementId: string
  canProduce: boolean
  note: string
  onProduce: () => void
  producing: boolean
  /** Every issued row for this engagement, oldest first as the API sends them. */
  documents: ValuationDocumentRow[]
  /** The produce response's `pdf_status`, so a missing PDF row shows WHY. */
  pdfStatus: string | null
  /** value_kind === 'concluded' — the frozen record is then a conclusion of value. */
  concluded: boolean
  /** Live snapshot version; null before the first finalize. */
  liveVersion: number | null
  /** Server says a retrievable .docx AND .pdf are on file for the live version. */
  documentsOnFile: boolean
  /** valuation_owner: the report is their unsigned estimate (VAL-WORDING-APPLY-1 H9). */
  owner?: boolean
}) {
  const liveRows = documents.filter(d => d.snapshot_version === liveVersion)
  const pdfMissing =
    liveVersion !== null && liveRows.length > 0 && !liveRows.some(d => d.media_type === 'application/pdf')

  return (
    <Card>
      <CardHeader><CardTitle>Produce document</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <Gate
          state={canProduce ? 'ok' : 'blocked'}
          label={canProduce ? 'Unlocked' : 'Locked'}
          detail={note}
        />
        <Button
          className="w-full"
          disabled={!canProduce || producing || documentsOnFile}
          onClick={onProduce}
        >
          {producing ? 'Producing…' : 'Produce document'}
        </Button>
        <EmailDocumentButton
          engagementId={engagementId}
          liveVersion={liveVersion}
          documentsOnFile={documentsOnFile}
        />
        {documentsOnFile && canProduce && (
          <p className="text-xs text-pal-ink-soft">
            {producedReasonText(liveVersion)}
          </p>
        )}
        {canProduce && !documentsOnFile && documents.length === 0 && (
          <p className="text-xs text-pal-ink-soft">
            The document is rendered from this frozen record — the weights, driver answers,
            benchmark row, inputs, rationale and signature that produced the number are printed
            with it.{' '}
            {concluded
              ? 'It is a CONCLUSION OF VALUE under an SSVS No. 1 valuation engagement.'
              // VAL-WORDING-APPLY-1 H9 (owner 10-29): the owner's report is their unsigned estimate.
              : owner
                ? 'It is your estimated value, unsigned. It is not a valuation under professional standards.'
                : 'It is a CALCULATED VALUE under an SSVS No. 1 calculation engagement, never a conclusion of value.'}
          </p>
        )}
        {documents.length > 0 && (
          <div className="space-y-2 border-t pt-3">
            <IssuedDocumentList
              engagementId={engagementId}
              documents={documents}
              liveVersion={liveVersion}
            />
            {pdfMissing && (
              <p className="text-xs text-pal-warning-text">
                PDF not available for this record. The Word document above is the
                signed record.{pdfStatus ? ` ${pdfStatus}` : ''}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
